import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const plans = [
    {
      name: 'Starter',
      description: 'For small companies getting started',
      price: '99',
      period: 'month',
      features: [
        'Up to 5 verifications/month',
        'KYC & KYB support',
        'UK region pack',
        'Email support',
        'Basic reporting',
        'Audit logs'
      ],
      cta: 'Get started',
      highlighted: false
    },
    {
      name: 'Professional',
      description: 'For growing practices',
      price: '299',
      period: 'month',
      features: [
        'Up to 50 verifications/month',
        'All region packs (UK, EU, US)',
        'Custom email templates',
        'Companies House integration',
        'Priority support',
        'Advanced reporting',
        'API access',
        'Custom branding',
        'Team collaboration'
      ],
      cta: 'Start free trial',
      highlighted: true
    },
    {
      name: 'Enterprise',
      description: 'For large organizations',
      price: 'Custom',
      period: '',
      features: [
        'Unlimited verifications',
        'All features in Professional',
        'Dedicated account manager',
        'SLA guarantee',
        'Custom integrations',
        'On-premise option',
        'Advanced security',
        'Training & onboarding'
      ],
      cta: 'Contact sales',
      highlighted: false
    }
  ];

  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Simple, transparent pricing</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the plan that fits your practice. All plans include core compliance features.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className={`rounded-2xl p-8 ${
                plan.highlighted 
                  ? 'bg-navy-900 text-white ring-4 ring-blue-500 ring-offset-4' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              <div className="mb-6">
                <h3 className={`text-xl font-semibold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.highlighted ? 'text-gray-300' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
              </div>

              <div className="mb-6">
                <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {plan.price !== 'Custom' && '£'}{plan.price}
                </span>
                {plan.period && (
                  <span className={`text-sm ${plan.highlighted ? 'text-gray-300' : 'text-gray-600'}`}>
                    /{plan.period}
                  </span>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start">
                    <Check className={`w-5 h-5 mr-3 flex-shrink-0 ${plan.highlighted ? 'text-blue-400' : 'text-green-500'}`} />
                    <span className={`text-sm ${plan.highlighted ? 'text-gray-300' : 'text-gray-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to="/login"
                className={`block w-full text-center py-3 rounded-lg font-medium transition-colors ${
                  plan.highlighted
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600">
            Need a custom plan?{' '}
            <Link to="/contact" className="text-blue-600 font-medium hover:text-blue-700">
              Contact us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
